import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, Award, Users, Building, History, Wand2, AlertTriangle } from 'lucide-react';
import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

interface AboutEditorProps {
  data: any;
  onChange: (data: any) => void;
}

const AboutEditor: React.FC<AboutEditorProps> = ({ data, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateContent = async (prompt: string) => {
    setLoading(true);
    setError(null);

    try {
      const completion = await openai.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: [
          {
            role: "system",
            content: "You are a professional copywriter specializing in service business websites. Write compelling and professional content."
          },
          {
            role: "user",
            content: prompt
          }
        ]
      });

      return completion.choices[0]?.message?.content || '';
    } catch (err) {
      console.error('Error generating content:', err);
      setError('Failed to generate content. Please try again.');
      return '';
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeamMember = () => {
    const newTeamMember = {
      name: '',
      title: '',
      bio: '',
      image: 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?auto=format&fit=crop&q=80',
    };

    onChange({
      ...data,
      about: {
        ...data.about,
        team: [...(data.about.team || []), newTeamMember]
      }
    });
  };

  const handleAddCertification = () => {
    const newCertification = {
      name: '',
      issuer: '',
      year: new Date().getFullYear(),
      image: ''
    };

    onChange({
      ...data,
      about: {
        ...data.about,
        certifications: [...(data.about.certifications || []), newCertification]
      }
    });
  };

  const handleAddMilestone = () => {
    const newMilestone = {
      year: new Date().getFullYear(),
      title: '',
      description: ''
    };

    onChange({
      ...data,
      about: {
        ...data.about,
        milestones: [...(data.about.milestones || []), newMilestone]
      }
    });
  };

  return (
    <div className="space-y-8">
      {/* Main Content */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Main Content</h3>
            <p className="text-sm text-gray-500">The primary content for your About section</p>
          </div>
          <button
            onClick={async () => {
              const content = await generateContent(
                `Write a professional about us section for ${data.header.title}, a service business. Include their commitment to quality and customer service. Keep it under 300 words.`
              );
              if (content) {
                onChange({
                  ...data,
                  about: {
                    ...data.about,
                    content
                  }
                });
              }
            }}
            disabled={loading}
            className="flex items-center px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
          >
            <Wand2 className="w-4 h-4 mr-1" />
            Generate Content
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Section Title
            </label>
            <input
              type="text"
              value={data.about.title}
              onChange={(e) => onChange({
                ...data,
                about: {
                  ...data.about,
                  title: e.target.value
                }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., About Us, Our Story, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Main Content
            </label>
            <textarea
              value={data.about.content}
              onChange={(e) => onChange({
                ...data,
                about: {
                  ...data.about,
                  content: e.target.value
                }
              })}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Tell your company's story..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Featured Image
            </label>
            <input
              type="url"
              value={data.about.image}
              onChange={(e) => onChange({
                ...data,
                about: {
                  ...data.about,
                  image: e.target.value
                }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter image URL"
            />
          </div>
        </div>
      </div>

      {/* Team Members */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
            <p className="text-sm text-gray-500">Showcase your key team members</p>
          </div>
          <button
            onClick={handleAddTeamMember}
            className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Team Member
          </button>
        </div>

        <div className="space-y-4">
          {(data.about.team || []).map((member: any, index: number) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-start">
                <div className="flex-1 mr-4 space-y-3">
                  <input
                    type="text"
                    value={member.name}
                    onChange={(e) => {
                      const updatedTeam = [...(data.about.team || [])];
                      updatedTeam[index].name = e.target.value;
                      onChange({
                        ...data,
                        about: {
                          ...data.about,
                          team: updatedTeam
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Full Name"
                  />
                  <input
                    type="text"
                    value={member.title}
                    onChange={(e) => {
                      const updatedTeam = [...(data.about.team || [])];
                      updatedTeam[index].title = e.target.value;
                      onChange({
                        ...data,
                        about: {
                          ...data.about,
                          team: updatedTeam
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Job Title"
                  />
                  <textarea
                    value={member.bio}
                    onChange={(e) => {
                      const updatedTeam = [...(data.about.team || [])];
                      updatedTeam[index].bio = e.target.value;
                      onChange({
                        ...data,
                        about: {
                          ...data.about,
                          team: updatedTeam
                        }
                      });
                    }}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Brief bio..."
                  />
                  <input
                    type="url"
                    value={member.image}
                    onChange={(e) => {
                      const updatedTeam = [...(data.about.team || [])];
                      updatedTeam[index].image = e.target.value;
                      onChange({
                        ...data,
                        about: {
                          ...data.about,
                          team: updatedTeam
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Profile Image URL"
                  />
                </div>
                <button
                  onClick={() => {
                    const updatedTeam = [...(data.about.team || [])];
                    updatedTeam.splice(index, 1);
                    onChange({
                      ...data,
                      about: {
                        ...data.about,
                        team: updatedTeam
                      }
                    });
                  }}
                  className="p-2 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Certifications */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Certifications</h3>
            <p className="text-sm text-gray-500">Display your professional certifications</p>
          </div>
          <button
            onClick={handleAddCertification}
            className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Certification
          </button>
        </div>

        <div className="space-y-4">
          {(data.about.certifications || []).map((cert: any, index: number) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-start">
                <div className="flex-1 mr-4 space-y-3">
                  <input
                    type="text"
                    value={cert.name}
                    onChange={(e) => {
                      const updatedCerts = [...(data.about.certifications || [])];
                      updatedCerts[index].name = e.target.value;
                      onChange({
                        ...data,
                        about: {
                          ...data.about,
                          certifications: updatedCerts
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Certification Name"
                  />
                  <input
                    type="text"
                    value={cert.issuer}
                    onChange={(e) => {
                      const updatedCerts = [...(data.about.certifications || [])];
                      updatedCerts[index].issuer = e.target.value;
                      onChange({
                        ...data,
                        about: {
                          ...data.about,
                          certifications: updatedCerts
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Issuing Organization"
                  />
                  <input
                    type="number"
                    value={cert.year}
                    onChange={(e) => {
                      const updatedCerts = [...(data.about.certifications || [])];
                      updatedCerts[index].year = parseInt(e.target.value);
                      onChange({
                        ...data,
                        about: {
                          ...data.about,
                          certifications: updatedCerts
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Year"
                  />
                  <input
                    type="url"
                    value={cert.image}
                    onChange={(e) => {
                      const updatedCerts = [...(data.about.certifications || [])];
                      updatedCerts[index].image = e.target.value;
                      onChange({
                        ...data,
                        about: {
                          ...data.about,
                          certifications: updatedCerts
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Certification Logo URL"
                  />
                </div>
                <button
                  onClick={() => {
                    const updatedCerts = [...(data.about.certifications || [])];
                    updatedCerts.splice(index, 1);
                    onChange({
                      ...data,
                      about: {
                        ...data.about,
                        certifications: updatedCerts
                      }
                    });
                  }}
                  className="p-2 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Company Milestones */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Company Milestones</h3>
            <p className="text-sm text-gray-500">Highlight your company's journey and achievements</p>
          </div>
          <button
            onClick={handleAddMilestone}
            className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Milestone
          </button>
        </div>

        <div className="space-y-4">
          {(data.about.milestones || []).map((milestone: any, index: number) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-start">
                <div className="flex-1 mr-4 space-y-3">
                  <input
                    type="number"
                    value={milestone.year}
                    onChange={(e) => {
                      const updatedMilestones = [...(data.about.milestones || [])];
                      updatedMilestones[index].year = parseInt(e.target.value);
                      onChange({
                        ...data,
                        about: {
                          ...data.about,
                          milestones: updatedMilestones
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Year"
                  />
                  <input
                    type="text"
                    value={milestone.title}
                    onChange={(e) => {
                      const updatedMilestones = [...(data.about.milestones || [])];
                      updatedMilestones[index].title = e.target.value;
                      onChange({
                        ...data,
                        about: {
                          ...data.about,
                          milestones: updatedMilestones
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Milestone Title"
                  />
                  <textarea
                    value={milestone.description}
                    onChange={(e) => {
                      const updatedMilestones = [...(data.about.milestones || [])];
                      updatedMilestones[index].description = e.target.value;
                      onChange({
                        ...data,
                        about: {
                          ...data.about,
                          milestones: updatedMilestones
                        }
                      });
                    }}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Milestone Description"
                  />
                </div>
                <button
                  onClick={() => {
                    const updatedMilestones = [...(data.about.milestones || [])];
                    updatedMilestones.splice(index, 1);
                    onChange({
                      ...data,
                      about: {
                        ...data.about,
                        milestones: updatedMilestones
                        }
                      });
                    }}
                  className="p-2 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-800 rounded-lg flex items-start">
          <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default AboutEditor;